<!-- Your app's shell. THIS FILE IS YOURS — the factory never replaces it.
     Everything imported from ./core is maintained by `appfactory upgrade`.

     The root: an IonApp, the outlet the router paints into, and the handful of
     things that must exist on every screen at once — the demo banner,
     onboarding, toasts, live regions. Screens live in src/pages/, wired up in
     src/router.js; the data they share is src/store.js. -->
<template>
  <IonApp>
    <!-- Says, permanently, that this is not your data. -->
    <DemoBanner v-if="isDemo" />

    <IonRouterOutlet />

    <!-- Shown once, ever. The last button is the first real thing to do. -->
    <!-- Version 2: the cards were rewritten for Legwork with screenshots, and
         the people who saw the three text cards should see these once. -->
    <Onboarding :cards="onboarding" :version="2" finish-label="See today's tickets" />

    <!-- Toasts, the undo bar, and the screen-reader live regions. -->
    <Overlays />
  </IonApp>
</template>

<script setup>
import { IonApp, IonRouterOutlet } from '@ionic/vue';
import { Onboarding, DemoBanner, Overlays, isDemo } from './core/ui';
import { startSettlement } from './lib/stores/settlement.js';
import router from './router.js';
import { riskProfile } from './store.js';

// After a deploy the shell a browser already holds points at chunk names that
// no longer exist, so the first tap on a lazy tab (Strategies, Backtest) fails
// with nothing on screen — the owner hit exactly this on the web after two
// deploys in a morning. Vite raises this event when a chunk fails to load;
// one reload picks up the new shell. Guarded so a genuinely broken deploy
// cannot loop.
if (typeof window !== 'undefined') {
  window.addEventListener('vite:preloadError', (event) => {
    const key = 'legwork.reloaded-for-chunk';
    let already = false;
    try { already = sessionStorage.getItem(key) === '1'; sessionStorage.setItem(key, '1'); } catch { /* private mode */ }
    if (already) return;
    event.preventDefault();
    window.location.reload();
  });
}

// Closing-price capture and CLV settle automatically, from wherever the app
// happens to be — see lib/stores/settlement.js.
startSettlement();

// Notifications, which are LOCAL: nothing here contacts a server and nothing
// costs a credit. Two jobs at boot, both cheap and neither of them asking the
// OS for anything (permission is asked in Settings, on the switch's own tap):
//
//   the tap handler  a notification opens the screen it is about. The mispriced
//                    alert carries `{ route: 'matches', chip: 'mispriced' }`,
//                    so the board opens already filtered to what buzzed — or
//                    `{ route: 'racing', … }` where every flagged price in it
//                    was a runner, because a card is not the board.
//   the reminder     re-scheduled on every launch. The schedule lives on the
//                    device and survives on its own, but a reinstall, an OS
//                    that dropped it, or a restore from a backup would leave
//                    the switch on and no reminder behind it.
//
// Imported late, and failing soft: `core/notify.js` reaches for the native
// bridge, and a shell that cannot load it must still render the app.
if (typeof window !== 'undefined') {
  import('./core/notify.js').then(({ onTap, scheduleDaily }) => {
    onTap((extra) => {
      if (extra?.route !== 'matches' && extra?.route !== 'racing') return;
      router.push({ name: extra.route, query: extra.chip ? { chip: extra.chip } : {} });
    });
    if (riskProfile.value.notifyDaily === true) scheduleDaily(riskProfile.value.notifyAt);
  }).catch(() => { /* no notifications on this runtime */ });
}

// First run. Five cards, four of them showing the screen they describe.
//
// The art is a REAL SCREENSHOT of this app, taken from /demo by
// `scripts/onboarding-shots.mjs` — re-run it whenever one of those four screens
// visibly changes, because a picture of the app is a promise about the app and
// a stale one is a lie a store review cannot catch. Every shot is 780 × 1040,
// which is why the dimensions are written here: the box is reserved before the
// file arrives, so nothing moves when it lands.
//
// The last card has no art on purpose. It is the 18+ notice, and a screenshot
// beside it would read as an advertisement for the thing being warned about.
const onboarding = [
  {
    // NO SCREENSHOT ON THIS CARD, for two reasons. The shot was of the dealt
    // pad ("Set 2 of 2", "Another set"), which Today no longer is — a stale
    // picture of the app is the lie the note above warns about. And the first
    // card's art is the largest thing a first run paints: drawn late inside the
    // opening modal, it WAS the app shell's Largest Contentful Paint (~8.5 s
    // under Lighthouse's throttle, 95% of it render delay). Re-shoot Today with
    // scripts/onboarding-shots.mjs and put a picture back only if it is of the
    // builder and does not move that number.
    title: 'Build the multi you want',
    body: 'Pick sports or games, lock any legs you like, and Legwork fills the rest from today’s prices, best value against the market first. Scroll for more.',
  },
  {
    title: 'Place bet, settle up later',
    body: 'Tap Place bet and the ticket is Pending in History; you still place it yourself, with your own bookie.',
    art: {
      src: '/onboarding/place.webp',
      alt: 'A Place bet confirmation on a ticket, and the same ticket a moment later stamped Placed.',
      width: 780,
      height: 1040,
    },
  },
  {
    title: 'Strategies you can read and tune',
    body: 'Eight ways to pick legs, each explained in plain words. Show or hide any of them on Today, and backtest before you trust it.',
    art: {
      src: '/onboarding/strategies.webp',
      alt: 'The Strategies tab: a strategy card with How it works, Hide on Today, Backtest and Tune buttons, above the Built-in pile.',
      width: 780,
      height: 1040,
    },
  },
  {
    title: 'You place it, not us',
    // The last sentence is the credit maths, and it is STATIC on purpose:
    // onboarding runs once, before anything is set up, and the six boards the
    // app ships with at two fetches a day is the figure a first-run phone will
    // actually see. `creditLine` in lib/credits.js prints the live version of
    // the same sentence everywhere it matters — the card Today shows before
    // there is a key, the Providers section, the foot of the board picker — so
    // if the arithmetic there ever moves, this line moves with it.
    body: 'Copy the legs into your own bookie. Legwork never takes a bet and never holds your money. Today’s board comes from your own free Odds API key: about 12 credits a day, 360 a month on a 500-credit free key.',
    art: {
      src: '/onboarding/ticket.webp',
      alt: 'A ticket in full, with Copy legs and Place bet at the foot of it.',
      width: 780,
      height: 1040,
    },
  },
  {
    title: 'Adults only. Most multis lose.',
    body: 'Every ticket shows its real chance of landing. For free, confidential support call Gambling Help on 1800 858 858.',
  },
];
</script>
